import clsx from 'clsx';
import { PolymorphicProps } from '../utils/types';

const DEFAULT_CONTAINER_TAG = 'div';

type ZeroStateBaseProps = {
  variant: 'row' | 'actionable';
  graphic: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
};

export type ZeroStateProps<T extends React.ElementType> = PolymorphicProps<
  T,
  ZeroStateBaseProps
>;

export const ZeroState = <
  T extends React.ElementType = typeof DEFAULT_CONTAINER_TAG,
>({
  variant,
  graphic,
  title,
  description,
  as,
  ...props
}: ZeroStateProps<T>) => {
  const Component = as || DEFAULT_CONTAINER_TAG;

  return (
    <Component
      className={clsx(
        'flex items-center',
        variant === 'actionable' &&
          'relative z-0 -mt-px gap-x-2 border border-teal-350 pr-4 outline-none hover:z-10 hover:border-b hover:border-teal-350 hover:bg-teal-200 focus-visible:z-20 focus-visible:border-rose-700 focus-visible:ring-1 focus-visible:ring-rose-700 group-first/card-component:rounded-t-lg group-last/card-component:rounded-b-lg',
        variant === 'row' && 'gap-x-3 bg-sage-200 pl-1',
      )}
      {...props}
    >
      <div
        className={clsx(
          'shrink-0 grow-0',
          variant === 'actionable' && 'max-w-32',
          variant === 'row' && 'size-12',
        )}
      >
        {graphic}
      </div>

      <div
        className={clsx(
          'flex flex-col items-start gap-y-0.5 text-sm',
          variant === 'actionable' && 'text-zinc-800',
          variant === 'row' && 'text-zinc-700',
        )}
      >
        <div className={clsx(variant === 'actionable' && 'font-semibold')}>
          {title}
        </div>

        {description && <div className="text-zinc-600">{description}</div>}
      </div>
    </Component>
  );
};
